import React, { useCallback, useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { Text } from '@atlaskit/primitives/compiled';
import Tag from '@atlaskit/tag';
import TagGroup from '@atlaskit/tag-group';

import type { UpdateWorkItemFieldsAction } from '../../../../../common/types';
import { FieldTooltipContent } from '../../../../../common/ui/FieldTooltipContent';
import {
	type RegisterTooltipProvider,
	useRegisterTooltipContentProvider,
} from '../../../../../common/utils/useRegisterTooltipContentProvider';

import type { labelsField_updateWorkItemFieldsActionRenderer$key } from './__generated__/labelsField_updateWorkItemFieldsActionRenderer.graphql';
import { parseLabels } from './parseLabels';

const messages = defineMessages({
	suggestedLabelsToAdd: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.labels-field.suggestedLabelsToAdd.non-final',
		defaultMessage: 'Suggested labels to add',
		description: 'Label for the suggested labels to add in the labels field',
	},
	suggestedLabelsToRemove: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.labels-field.suggestedLabelsToRemove.non-final',
		defaultMessage: 'Suggested labels to remove',
		description: 'Label for the suggested labels to remove in the labels field',
	},
	updateLabels: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.labels-field.updateLabels.non-final',
		defaultMessage: 'Update labels',
		description: 'Label for the update labels in the labels field',
	},
});

export type Props = {
	queryRef: labelsField_updateWorkItemFieldsActionRenderer$key;
	action: UpdateWorkItemFieldsAction;
	registerTooltipProvider?: RegisterTooltipProvider;
};

export const LabelsField = ({
	queryRef,
	action,
	registerTooltipProvider,
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const data = useFragment(
		graphql`
			fragment labelsField_updateWorkItemFieldsActionRenderer on JiraLabelsField {
				fieldId
				name
				selectedLabelsConnection {
					edges {
						node {
							name
						}
					}
				}
			}
		`,
		queryRef,
	);

	const isFieldRenderable =
		data?.fieldId === action?.data?.fieldKey && action?.data?.fieldKey === 'labels';

	const suggestedLabels = useMemo(
		() => parseLabels(action.data.fieldValue),
		[action?.data?.fieldValue],
	);

	const currentLabels = useMemo(
		() => data?.selectedLabelsConnection?.edges?.map((edge) => edge?.node?.name) ?? [],
		[data?.selectedLabelsConnection],
	);

	const addedLabels = useMemo(
		() => suggestedLabels?.filter((label) => !currentLabels?.includes(label ?? '')) ?? [],
		[suggestedLabels, currentLabels],
	);
	const removedLabels = useMemo(
		() => currentLabels.filter((label) => !suggestedLabels?.includes(label ?? '')),
		[currentLabels, suggestedLabels],
	);

	const toolTipContentProvider = useCallback(() => {
		const currentLabels =
			data?.selectedLabelsConnection?.edges?.map((edge) => edge?.node?.name) ?? [];

		const current = currentLabels.length ? (
			<TagGroup>
				{currentLabels
					.filter((label) => label !== undefined && label !== null)
					.map((label) => (
						<Tag key={label} text={label} isRemovable={false} />
					))}
			</TagGroup>
		) : null;

		const suggestedToAdd = addedLabels?.length ? (
			<TagGroup>
				{addedLabels
					.filter((label) => label !== undefined && label !== null)
					.map((label) => (
						<Tag key={label} text={label} isRemovable={false} />
					))}
			</TagGroup>
		) : null;

		const suggestedToRemove = removedLabels?.length ? (
			<TagGroup>
				{removedLabels
					.filter((label) => label !== undefined && label !== null)
					.map((label) => (
						<Tag key={label} text={label} isRemovable={false} />
					))}
			</TagGroup>
		) : null;

		return (
			<FieldTooltipContent
				reasoning={action?.data?.reasoning}
				current={current}
				additionalSuggestions={[
					{
						suggested: addedLabels.length > 0 ? suggestedToAdd : null,
						label: formatMessage(messages.suggestedLabelsToAdd),
						testIdSuffix: 'suggested-labels-to-add',
					},
					{
						suggested: removedLabels.length > 0 ? suggestedToRemove : null,
						label: formatMessage(messages.suggestedLabelsToRemove),
						testIdSuffix: 'suggested-labels-to-remove',
					},
				]}
				fieldName={data?.name}
			/>
		);
	}, [
		action?.data?.reasoning,
		data?.name,
		data?.selectedLabelsConnection?.edges,
		formatMessage,
		addedLabels,
		removedLabels,
	]);

	useRegisterTooltipContentProvider({
		registerTooltipProvider,
		fieldId: action?.data?.fieldKey,
		provider: toolTipContentProvider,
		enabled: isFieldRenderable,
	});

	if (!isFieldRenderable) {
		return null;
	}

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-fields-action-renderer.ui.action-card.card-fields-list.labels-field"
		>
			{formatMessage(messages.updateLabels)}
		</Text>
	);
};
